import React, { useState } from "react";
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";

interface LoginModalProps {
  show: boolean;
  onLogin: (apiKey: string) => void;
  onCancel?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ show, onLogin, onCancel }) => {
  const [apiKey, setApiKey] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey.trim()) {
      setError("Please enter an API key");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Test the API key by making a simple request
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}expenditure-forecast/projects`,
        {
          headers: { Authorization: apiKey },
          validateStatus: () => true,
        }
      );

      if (response.status === 200) {
        onLogin(apiKey);
        setApiKey(""); // Clear the form
        setError("");
      } else if (response.status === 401) {
        setError("Invalid API key. Please try again.");
      } else {
        setError("Unable to connect to server. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  const handleClose = () => {
    if (!loading && onCancel) {
      setApiKey("");
      setError("");
      onCancel();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={!loading}
      centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>API Authentication Required</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted mb-3">
            Please enter your API key to access the application.
          </p>

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Form.Group>
            <Form.Label>API Key</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter your API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              autoFocus
              required
            />
            <Form.Text className="text-muted">
              This key will be used for all API requests during your session.
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          {onCancel && (
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !apiKey.trim()}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Authenticating...
              </>
            ) : (
              "Login"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LoginModal;
